import React from 'react';
import { Check } from 'lucide-react';

interface Step {
  id: string;
  label: string;
  completed: boolean;
  current: boolean;
  enabled: boolean;
}

interface StepProgressProps {
  steps: Step[];
  onStepClick?: (stepId: string) => void;
}

export function StepProgress({ steps, onStepClick }: StepProgressProps) {
  return (
    <div style={{
      width: '100%',
      background: 'rgba(255,255,255,0.03)',
      border: '1px solid rgba(255,255,255,0.07)',
      borderRadius: 16,
      padding: '20px 24px',
      marginBottom: 24,
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {steps.map((step, index) => (
          <React.Fragment key={step.id}>
            {/* Step Item */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
              {/* Circle */}
              <button
                onClick={() => onStepClick && step.enabled && onStepClick(step.id)}
                disabled={!step.enabled}
                style={{
                  position: 'relative',
                  width: 44,
                  height: 44,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: 8,
                  cursor: step.enabled ? 'pointer' : 'not-allowed',
                  transition: 'all 0.25s ease',
                  border: 'none',
                  transform: step.current && !step.completed ? 'scale(1.1)' : 'scale(1)',
                  ...(step.completed
                    ? {
                        background: 'linear-gradient(135deg,#059669,#10B981)',
                        boxShadow: '0 4px 16px rgba(16,185,129,0.35)',
                        color: 'white',
                      }
                    : step.current
                    ? {
                        background: 'linear-gradient(135deg,#6366F1,#8B5CF6)',
                        boxShadow: '0 4px 20px rgba(99,102,241,0.5)',
                        color: 'white',
                      }
                    : step.enabled
                    ? {
                        background: 'rgba(255,255,255,0.07)',
                        border: '1px solid rgba(255,255,255,0.12)',
                        color: 'var(--text-secondary)',
                      }
                    : {
                        background: 'rgba(255,255,255,0.03)',
                        border: '1px solid rgba(255,255,255,0.06)',
                        color: 'var(--text-muted)',
                      }),
                }}
              >
                {step.completed ? (
                  <Check style={{ width: 20, height: 20 }} />
                ) : (
                  <span style={{ fontSize: 13, fontWeight: 700 }}>{index + 1}</span>
                )}

                {/* Pulse ring for current step */}
                {step.current && !step.completed && (
                  <span style={{
                    position: 'absolute',
                    inset: -4,
                    borderRadius: '50%',
                    border: '2px solid rgba(99,102,241,0.4)',
                    animation: 'ping 1.8s cubic-bezier(0,0,0.2,1) infinite',
                  }} />
                )}
              </button>

              {/* Label */}
              <span style={{
                fontSize: 11,
                fontWeight: step.current ? 600 : 400,
                textAlign: 'center',
                maxWidth: 100,
                lineHeight: 1.4,
                color: step.completed
                  ? '#34D399'
                  : step.current
                  ? '#818CF8'
                  : step.enabled
                  ? 'var(--text-secondary)'
                  : 'var(--text-muted)',
              }}>
                {step.label}
              </span>
            </div>

            {/* Connector Line */}
            {index < steps.length - 1 && (
              <div style={{ flex: 1, height: 2, maxWidth: 60, position: 'relative', margin: '0 4px', marginBottom: 24 }}>
                <div style={{
                  position: 'absolute', inset: 0,
                  background: 'rgba(255,255,255,0.07)',
                  borderRadius: 99,
                }} />
                <div style={{
                  position: 'absolute', inset: 0,
                  borderRadius: 99,
                  background: step.completed
                    ? 'linear-gradient(90deg,#10B981,#34D399)'
                    : 'transparent',
                  width: step.completed ? '100%' : '0%',
                  transition: 'width 0.5s ease',
                }} />
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}
